// Mirror of api/src/replicache/keys.ts.  Replicache keys are flat strings; we
// use one prefix per entity so `scanByPrefix` works.

export const GROUP_PREFIX = "group/";
export const ITEM_PREFIX = "item/";
export const TIME_ENTRY_PREFIX = "time/";
export const ATTACHMENT_PREFIX = "attach/";
export const FOLDER_PREFIX = "folder/";
export const NOTE_PREFIX = "note/";
export const CANVAS_PREFIX = "canvas/";
export const SCENE_PREFIX = "scene/";

export const groupKey = (id: string) => `${GROUP_PREFIX}${id}`;
export const itemKey = (id: string) => `${ITEM_PREFIX}${id}`;
export const timeEntryKey = (id: string) => `${TIME_ENTRY_PREFIX}${id}`;
export const attachmentKey = (id: string) => `${ATTACHMENT_PREFIX}${id}`;
export const folderKey = (id: string) => `${FOLDER_PREFIX}${id}`;
export const noteKey = (id: string) => `${NOTE_PREFIX}${id}`;
export const canvasKey = (id: string) => `${CANVAS_PREFIX}${id}`;
export const sceneKey = (id: string) => `${SCENE_PREFIX}${id}`;

export type Group = {
  id: string;
  // Owner of the group.  When this !== the current user, the group is
  // shared with them via the new user-targeted shares (Phase E).  UI uses
  // this to badge "shared from @owner" rows in the sidebar.
  ownerId: string;
  name: string;
  color: string;
  icon: string | null;
  sortKey: string;
  // The folder this group is filed under, or null for top-level.  Only
  // ever set on groups the current user owns; shared-in groups always
  // arrive with folderId=null (folders are owner-private organisation).
  folderId: string | null;
  createdAt: string;
  updatedAt: string;
  // Server-stamped monotonic row version.  Read by the sync-integrity
  // hasher in frontend/src/replicache/instance.ts — UI never depends on it.
  version: number;
};

export type Folder = {
  id: string;
  name: string;
  sortKey: string;
  createdAt: string;
  updatedAt: string;
  version: number;
};

export type Note = {
  id: string;
  // Mirrors Group.ownerId — distinguishes "mine" from "shared with me"
  // (notes aren't share-able in V1 but the field is here so the client
  // doesn't need to special-case notes once sharing arrives).
  ownerId: string;
  name: string;
  color: string;
  icon: string | null;
  body: string;
  sortKey: string;
  folderId: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
};

export type Canvas = {
  id: string;
  ownerId: string;
  name: string;
  strokes: string;
  sortKey: string;
  createdAt: string;
  updatedAt: string;
  version: number;
};

// Excalidraw scene — a rich whiteboard that lives alongside Notes and Canvas.
export type Scene = {
  id: string;
  // Mirrors Canvas.ownerId.  When ownerId !== current user the scene is
  // shared in; the UI badges it "shared from @owner" and only allows edits
  // when the share grants them.
  ownerId: string;
  name: string;
  // JSON-encoded Excalidraw document: { v, elements, appState }.  Image
  // bytes are offloaded to MinIO (scene_files); elements carry fileId refs.
  scene: string;
  sortKey: string;
  createdAt: string;
  updatedAt: string;
  version: number;
};

export type ItemStatus =
  | "not_started"
  | "in_progress"
  | "blocked"
  | "done"
  | "cancelled";

export type ItemPriority = "low" | "medium" | "high";
export type ItemRecurrence = "daily" | "weekly" | "monthly";

export type Item = {
  id: string;
  // See Group.ownerId — same semantics for items.  Shared items via an
  // edit-share appear in the recipient's cache with the share owner's id.
  ownerId: string;
  groupId: string;
  parentId: string | null;
  type: "task" | "header";
  title: string;
  notes: string;
  status: ItemStatus;
  priority: ItemPriority | null;
  isFocused: boolean;
  startDate: string | null;
  dueDate: string | null;
  recurrence: ItemRecurrence | null;
  reportingTo: string | null;
  sortKey: string;
  isComplete: boolean;
  createdAt: string;
  updatedAt: string;
  version: number;
};

export type TimeEntry = {
  id: string;
  itemId: string;
  // ISO datetime strings (with timezone) — local-time inputs are converted at
  // the form boundary.
  startedAt: string;
  endedAt: string;
  // Denormalised for aggregation; computed by client mutator and by the
  // server's GENERATED column (which is authoritative).
  durationMinutes: number;
  note: string;
  createdAt: string;
  updatedAt: string;
  version: number;
};

export type AttachmentStatus = "pending" | "uploaded" | "failed";

export type Attachment = {
  id: string;
  itemId: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  // Path in MinIO.  The blob lives in IndexedDB (under this same key) until
  // `uploadStatus` flips to "uploaded".
  storageKey: string;
  uploadStatus: AttachmentStatus;
  createdAt: string;
  updatedAt: string;
  version: number;
};
